import React, { useEffect, useState } from "react";
import IndexNav from "../../IndexNav";
import { useHistory } from "react-router-dom";
import { getReviews } from "../../../Services/engagementService";
import {
  Actions,
  Button,
  Container,
  Content,
  CtaAction,
  CtaSection,
  CtaText,
  CtaTitle,
  Eyebrow,
  Feature,
  FeatureGrid,
  FeatureSection,
  FeatureText,
  FeatureTitle,
  Footer,
  FooterInner,
  FooterLink,
  FooterLinks,
  FooterText,
  FloatingNote,
  HeroSection,
  LeftSide,
  LeftWrapper,
  Meta,
  MetaItem,
  ProductHuntRow,
  ReviewAuthor,
  ReviewCard,
  ReviewsGrid,
  ReviewsSection,
  ReviewStars,
  ReviewText,
  RightSide,
  SectionText,
  SectionTitle,
  Step,
  StepText,
  StepTitle,
  SvgItem,
  Text,
  Title,
  VisualPanel,
  Workflow,
} from "./Styled";

const features = [
  {
    title: "Boards built for real teams",
    text: "Create workspaces for product, engineering, design, and operations with clear ownership from day one.",
  },
  {
    title: "Card-level execution control",
    text: "Track priorities, deadlines, members, labels, and progress details where work actually happens.",
  },
  {
    title: "Faster collaboration loops",
    text: "Invite teammates instantly, share board context, and keep status visible without endless follow-up.",
  },
  {
    title: "Clean board discovery",
    text: "Search and jump between boards in seconds so active projects stay easy to find as you scale.",
  },
  {
    title: "Built-in activity context",
    text: "See what changed, who changed it, and what is next so teams can recover context quickly.",
  },
  {
    title: "Simple setup, zero clutter",
    text: "Start with a title and background, invite teammates, and move directly into work in under a minute.",
  },
];

const workflow = [
  {
    title: "1. Create",
    text: "Start a board and define a clear outcome for your sprint, launch, or campaign.",
  },
  {
    title: "2. Organize",
    text: "Break work into lists and cards so progress is visible across every stage.",
  },
  {
    title: "3. Collaborate",
    text: "Invite members and align ownership directly inside the workstream.",
  },
  {
    title: "4. Deliver",
    text: "Track progress and close tasks with confidence as the team moves forward.",
  },
];

const fallbackReviews = [
  {
    _id: "fallback-1",
    rating: 5,
    content: "PepHub Board made it easier for our team to plan, assign, and ship tasks in one flow.",
    name: "Product Team",
  },
  {
    _id: "fallback-2",
    rating: 5,
    content: "Fast to set up, easy to use, and clear enough for everyone to stay aligned.",
    name: "Operations Team",
  },
  {
    _id: "fallback-3",
    rating: 5,
    content: "The board view helps us keep momentum without needing extra status meetings.",
    name: "Growth Team",
  },
];

const Index = () => {
  const history = useHistory();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const loadReviews = async () => {
      try {
        const response = await getReviews();
        setReviews(response.slice(0, 3));
      } catch (error) {
        setReviews([]);
      }
    };
    loadReviews();
  }, []);

  const renderRating = (rating) => {
    const safeRating = Math.max(1, Math.min(5, Number(rating) || 5));
    return `${"*".repeat(safeRating)} (${safeRating}/5)`;
  };

  const visibleReviews = reviews.length ? reviews : fallbackReviews;

  return (
    <>
      <IndexNav />
      <Container>
        <Content>
          <HeroSection>
            <LeftSide>
              <LeftWrapper>
                <Eyebrow>Project management without the drag</Eyebrow>
                <Title>Build faster. Track smarter. Keep every team in sync.</Title>
                <Text>
                  PepHub Board gives teams a clear way to plan, execute, and ship work together from
                  one shared space.
                </Text>
                <Actions>
                  <Button onClick={() => history.push("/register")}>Sign up free</Button>
                  <Button secondary onClick={() => history.push("/login")}>
                    Open existing workspace
                  </Button>
                </Actions>
                <Meta>
                  <MetaItem>Board setup in under 60 seconds</MetaItem>
                  <MetaItem>Invite teammates with one email</MetaItem>
                  <MetaItem>Track every stage from idea to done</MetaItem>
                </Meta>
                <ProductHuntRow>
                  <a
                    href="https://www.producthunt.com/products/pephub-board?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-pephub-board"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      alt="PepHub Board - Build faster. Track smarter. | Product Hunt"
                      width="250"
                      height="54"
                      src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1120833&theme=neutral&t=1775853489367"
                    />
                  </a>
                </ProductHuntRow>
              </LeftWrapper>
            </LeftSide>
            <RightSide>
              <VisualPanel>
                <SvgItem
                  alt="PepHub Board workspace preview"
                  src="https://images.ctfassets.net/rz1oowkt5gyp/5QIzYxue6b7raOnVFtMyQs/113acb8633ee8f0c9cb305d3a228823c/hero.png?w=1200&fm=webp"
                />
                <FloatingNote>From idea to execution in one board view</FloatingNote>
              </VisualPanel>
            </RightSide>
          </HeroSection>

          <FeatureSection>
            <SectionTitle>Everything your team needs to move work forward</SectionTitle>
            <SectionText>
              PepHub Board combines planning, ownership, and day-to-day execution in a focused
              interface. You spend less time coordinating and more time delivering.
            </SectionText>
            <FeatureGrid>
              {features.map((feature) => (
                <Feature key={feature.title}>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureText>{feature.text}</FeatureText>
                </Feature>
              ))}
            </FeatureGrid>
          </FeatureSection>

          <FeatureSection>
            <SectionTitle>How teams use PepHub Board</SectionTitle>
            <Workflow>
              {workflow.map((item) => (
                <Step key={item.title}>
                  <StepTitle>{item.title}</StepTitle>
                  <StepText>{item.text}</StepText>
                </Step>
              ))}
            </Workflow>
          </FeatureSection>

          <ReviewsSection>
            <SectionTitle>What teams are saying</SectionTitle>
            <SectionText>
              Reviews below are submitted by users and published automatically on the website.
            </SectionText>
            <ReviewsGrid>
              {visibleReviews.map((review) => (
                <ReviewCard key={review._id}>
                  <ReviewStars>{renderRating(review.rating)}</ReviewStars>
                  <ReviewText>{review.content}</ReviewText>
                  <ReviewAuthor>{review.name}</ReviewAuthor>
                </ReviewCard>
              ))}
            </ReviewsGrid>
          </ReviewsSection>

          <CtaSection>
            <div>
              <CtaTitle>Ready to organize work with better visibility?</CtaTitle>
              <CtaText>Launch your board and start collaborating in minutes.</CtaText>
            </div>
            <CtaAction onClick={() => history.push("/register")}>Create your free account</CtaAction>
          </CtaSection>

          <Footer>
            <FooterInner>
              <FooterText>© {new Date().getFullYear()} PepHub Boards. All rights reserved.</FooterText>
              <FooterLinks>
                <FooterLink onClick={() => history.push("/terms")}>Terms & Conditions</FooterLink>
                <FooterLink onClick={() => history.push("/privacy")}>Privacy Policy</FooterLink>
              </FooterLinks>
            </FooterInner>
          </Footer>
        </Content>
      </Container>
    </>
  );
};

export default Index;
